import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Euro,
  Calendar,
  MessageSquare,
  CheckCircle2,
  Clock,
  User,
  PhoneCall,
  Send,
  ShieldAlert,
  ShieldCheck,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const CONTACT_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  NOT_CONTACTED: { label: "Non contacté", className: "bg-gray-100 text-gray-700 border-gray-300" },
  CONTACTED: { label: "Contacté", className: "bg-yellow-50 text-yellow-700 border-yellow-300" },
  REPLIED: { label: "A répondu", className: "bg-blue-50 text-blue-700 border-blue-300" },
  CONVERTED: { label: "Converti", className: "bg-green-50 text-green-700 border-green-300" },
};

const CATEGORY_LABELS: Record<string, string> = {
  RENOVATION: "Rénovation",
  EXTENSION: "Extension",
  TOITURE: "Toiture",
  PLOMBERIE: "Plomberie",
  ELECTRICITE: "Électricité",
  PEINTURE: "Peinture",
  MACONNERIE: "Maçonnerie",
  AMENAGEMENT_EXTERIEUR: "Aménagement extérieur",
  SALLE_DE_BAIN: "Salle de bain",
  CUISINE: "Cuisine",
  CARRELAGE: "Carrelage",
  CLIMATISATION: "Climatisation",
  ISOLATION: "Isolation",
  MENUISERIE: "Menuiserie",
  FACADE: "Façade",
  TERRASSEMENT: "Terrassement",
  PISCINE: "Piscine",
  DOMOTIQUE: "Domotique",
  AUTRE: "Autre",
};

function formatDate(d: string | Date | null | undefined) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
}

function formatBudget(min?: number | null, max?: number | null): string {
  if (!min && !max) return "Non renseigné";
  if (min && max) return `${min.toLocaleString("fr-FR")} – ${max.toLocaleString("fr-FR")} €`;
  if (min) return `≥ ${min.toLocaleString("fr-FR")} €`;
  if (max) return `≤ ${max.toLocaleString("fr-FR")} €`;
  return "—";
}

export default function ParticulierDetail() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const hasWrite = canWrite(user?.staffRole);

  const [opsNotes, setOpsNotes] = useState("");
  const [newStatus, setNewStatus] = useState<string>("");
  const [callNotes, setCallNotes] = useState("");
  const [showCallForm, setShowCallForm] = useState(false);
  const [blockDialog, setBlockDialog] = useState(false);
  const [blockReason, setBlockReason] = useState("");
  const [userBlocked, setUserBlocked] = useState<boolean | null>(null);

  // Fetch projet via listParticuliers (single item lookup using page/limit=1 is not ideal;
  // use privateProjects.getById which returns full project info including created by)
  const { data: projectData, isLoading, refetch } = trpc.privateProjects.getById.useQuery(
    { id: projectId! },
    { enabled: !!projectId }
  );

  // Hydrate l'état blocked depuis le serveur au chargement (sinon affiche "Bloquer" même si déjà bloqué).
  // NB : ce useEffect DOIT rester APRÈS la déclaration de `projectData`. Le
  // référencer dans le tableau de deps avant sa déclaration provoque un TDZ
  // ("Cannot access 'projectData' before initialization" — Sentry OPS-1).
  useEffect(() => {
    const cb = (projectData as any)?.CreatedBy;
    if (cb && typeof cb.isBlocked === "boolean") {
      setUserBlocked(cb.isBlocked);
    }
  }, [projectData]);

  const sendEmailMutation = trpc.admin.travauxbtp.sendContactEmail.useMutation({
    onSuccess: () => {
      toast.success("Email envoyé avec succès");
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const logCallMutation = trpc.admin.travauxbtp.logContactAttempt.useMutation({
    onSuccess: () => {
      toast.success("Appel enregistré");
      setCallNotes("");
      setShowCallForm(false);
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const updateStatusMutation = trpc.admin.travauxbtp.updateContactStatus.useMutation({
    onSuccess: () => {
      toast.success("Statut mis à jour");
      setOpsNotes("");
      setNewStatus("");
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const blockMutation = trpc.admin.travauxbtp.blockUser.useMutation({
    onSuccess: () => {
      toast.success("Compte bloqué");
      setBlockDialog(false);
      setBlockReason("");
      setUserBlocked(true);
    },
    onError: (err) => toast.error(err.message),
  });

  const unblockMutation = trpc.admin.travauxbtp.unblockUser.useMutation({
    onSuccess: () => {
      toast.success("Compte débloqué");
      setUserBlocked(false);
    },
    onError: (err) => toast.error(err.message),
  });

  const { data: contactLogsData, isLoading: logsLoading } = trpc.admin.travauxbtp.getContactLogs.useQuery(
    { privateProjectId: projectId! },
    { enabled: !!projectId }
  );

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="space-y-6 max-w-4xl">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </AdminLayout>
    );
  }

  if (!projectData) {
    return (
      <AdminLayout>
        <div className="text-center py-20 text-muted-foreground">Projet introuvable.</div>
      </AdminLayout>
    );
  }

  const particulier = projectData.CreatedBy;
  const contactStatusConfig =
    CONTACT_STATUS_CONFIG[(projectData as any).opsContactStatus ?? "NOT_CONTACTED"] ??
    CONTACT_STATUS_CONFIG["NOT_CONTACTED"];

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Retour
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold">{projectData.title}</h1>
            <p className="text-sm text-muted-foreground">Détail particulier — outreach OPS</p>
          </div>
          <Badge className={contactStatusConfig.className + " border"}>
            {contactStatusConfig.label}
          </Badge>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Profil particulier */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <User className="h-4 w-4" />
                Profil particulier
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <span className="font-medium">
                  {particulier?.firstName} {particulier?.lastName}
                </span>
              </div>
              {particulier?.email && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Mail className="h-3.5 w-3.5 shrink-0" />
                  <a href={`mailto:${particulier.email}`} className="hover:underline">
                    {particulier.email}
                  </a>
                </div>
              )}
              {particulier?.phone && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Phone className="h-3.5 w-3.5 shrink-0" />
                  <a href={`tel:${particulier.phone}`} className="hover:underline">
                    {particulier.phone}
                  </a>
                </div>
              )}
              {(projectData.city || projectData.department) && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="h-3.5 w-3.5 shrink-0" />
                  {[projectData.city, projectData.department].filter(Boolean).join(", ")}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Projet déposé */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Projet déposé
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {projectData.category && (
                <div>
                  <span className="text-muted-foreground">Catégorie : </span>
                  <span className="font-medium">{CATEGORY_LABELS[projectData.category] ?? projectData.category}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Euro className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span>{formatBudget(projectData.budgetMin, projectData.budgetMax)}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="h-3.5 w-3.5 shrink-0" />
                Déposé le {formatDate(projectData.createdAt)}
              </div>
              {projectData.description && (
                <p className="text-muted-foreground leading-relaxed pt-1">{projectData.description}</p>
              )}
              {(projectData as any).lastContactAt && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="h-3.5 w-3.5 shrink-0" />
                  Dernier contact : {formatDate((projectData as any).lastContactAt)}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Contact Action Panel */}
        {hasWrite && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Actions de contact</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Email templates */}
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Envoyer un email template</p>
                <div className="flex flex-wrap gap-2">
                  {(["INTRO", "FOLLOW_UP", "QUALIFICATION"] as const).map((tpl) => (
                    <Button
                      key={tpl}
                      variant="outline"
                      size="sm"
                      disabled={sendEmailMutation.isPending}
                      onClick={() =>
                        sendEmailMutation.mutate({
                          privateProjectId: projectId!,
                          templateType: tpl,
                        })
                      }
                    >
                      <Mail className="h-3.5 w-3.5 mr-1.5" />
                      {tpl === "INTRO" ? "Introduction" : tpl === "FOLLOW_UP" ? "Relance" : "Qualification"}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Log call */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-muted-foreground">Enregistrer un appel</p>
                  {!showCallForm && (
                    <Button variant="outline" size="sm" onClick={() => setShowCallForm(true)}>
                      <Phone className="h-3.5 w-3.5 mr-1.5" />
                      Ajouter appel
                    </Button>
                  )}
                </div>
                {showCallForm && (
                  <div className="space-y-2">
                    <Textarea
                      placeholder="Notes sur l'appel (facultatif)..."
                      value={callNotes}
                      onChange={(e) => setCallNotes(e.target.value)}
                      rows={2}
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        disabled={logCallMutation.isPending}
                        onClick={() =>
                          logCallMutation.mutate({
                            privateProjectId: projectId!,
                            channel: "PHONE",
                            notes: callNotes || undefined,
                          })
                        }
                      >
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                        Enregistrer
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setShowCallForm(false)}>
                        Annuler
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Change status */}
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Changer le statut</p>
                <div className="flex flex-wrap gap-2 items-start">
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Nouveau statut..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NOT_CONTACTED">Non contacté</SelectItem>
                      <SelectItem value="CONTACTED">Contacté</SelectItem>
                      <SelectItem value="REPLIED">A répondu</SelectItem>
                      <SelectItem value="CONVERTED">Converti</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex-1 min-w-[200px]">
                    <Textarea
                      placeholder="Notes OPS (facultatif)..."
                      value={opsNotes}
                      onChange={(e) => setOpsNotes(e.target.value)}
                      rows={2}
                    />
                  </div>
                  <Button
                    size="sm"
                    disabled={!newStatus || updateStatusMutation.isPending}
                    onClick={() =>
                      updateStatusMutation.mutate({
                        privateProjectId: projectId!,
                        status: newStatus as any,
                        opsNotes: opsNotes || undefined,
                      })
                    }
                  >
                    Appliquer
                  </Button>
                </div>
              </div>

              {/* OPS Notes display */}
              {(projectData as any).opsNotes && (
                <div className="rounded-md border bg-muted/40 p-3 text-sm">
                  <p className="text-xs font-medium text-muted-foreground mb-1">Notes OPS</p>
                  <p>{(projectData as any).opsNotes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Contact history */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Historique des contacts
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {logsLoading ? (
              <div className="text-center text-sm text-muted-foreground py-4">Chargement…</div>
            ) : !contactLogsData?.logs?.length ? (
              <p className="text-sm text-muted-foreground text-center py-4">Aucun contact enregistré</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(contactLogsData.logs as any[]).map((log: any) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        <div className="flex items-center gap-1.5 text-sm">
                          {log.type === "EMAIL" ? (
                            <Send className="h-3.5 w-3.5 text-primary" />
                          ) : (
                            <PhoneCall className="h-3.5 w-3.5 text-success" />
                          )}
                          {log.type === "EMAIL" ? "Email" : "Appel"}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
                        {log.notes ?? "—"}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {formatDate(log.sentAt ?? log.createdAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
        {/* Modération compte */}
        {hasWrite && particulier?.id && (
          <Card className="border-orange-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2 text-orange-700">
                <ShieldAlert className="h-4 w-4" />
                Modération compte
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {userBlocked === true ? (
                <div className="flex items-center gap-3">
                  <Badge className="bg-red-100 text-red-700 border-red-300 border">Bloqué</Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={unblockMutation.isPending}
                    onClick={() =>
                      unblockMutation.mutate({ userId: particulier.id, reason: "Déblocage manuel OPS" })
                    }
                  >
                    <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
                    {unblockMutation.isPending ? "Déblocage…" : "Débloquer"}
                  </Button>
                </div>
              ) : userBlocked === false ? (
                <div className="flex items-center gap-3">
                  <Badge className="bg-green-100 text-green-700 border-green-300 border">Actif</Badge>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setBlockDialog(true)}
                  >
                    <ShieldAlert className="h-3.5 w-3.5 mr-1.5" />
                    Bloquer ce compte
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <p className="text-sm text-muted-foreground">Actions de modération sur le compte particulier</p>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setBlockDialog(true)}
                  >
                    <ShieldAlert className="h-3.5 w-3.5 mr-1.5" />
                    Bloquer ce compte
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={unblockMutation.isPending}
                    onClick={() =>
                      unblockMutation.mutate({ userId: particulier.id, reason: "Déblocage manuel OPS" })
                    }
                  >
                    <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
                    Débloquer
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Block user dialog */}
      <Dialog open={blockDialog} onOpenChange={setBlockDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <ShieldAlert className="h-5 w-5" />
              Bloquer ce compte
            </DialogTitle>
            <DialogDescription>
              {particulier?.firstName} {particulier?.lastName} — cette action empêche l'utilisateur d'accéder à la plateforme.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">Raison (obligatoire, min. 10 caractères)</label>
            <Textarea
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              placeholder="Raison du blocage…"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlockDialog(false)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              disabled={blockReason.length < 10 || blockMutation.isPending}
              onClick={() => {
                if (!particulier?.id) return;
                blockMutation.mutate({ userId: particulier.id, reason: blockReason });
              }}
            >
              {blockMutation.isPending ? "Blocage…" : "Confirmer le blocage"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
